// Listings.tsx (Fixed with exact schema naming)
import { useCallback } from "react";
import { Home, Building2, Clock, MapPin, Star, Wifi, Tv, Wind, Coffee, Car, Users, Eye, ShieldCheckIcon, List, SearchIcon, Loader2 } from "lucide-react";
import CustomListingsPage from "@/MY_COMPONENTS/CustomListingsPage";
import ListingCardGrid from "./ListingCardGrid";
import ListingCardList from "./ListingCardList";
import ListingCardMap from "./ListingCardMap";
import { MY_ENDPOINTS } from "@raashidh/shared";
import { useMyDialog } from "@/context/MyDialogProvider";
import ListingDetails from "./ListingDetails";
import MyDialogButton from "@/MY_COMPONENTS/MyDialogButton";
import AddListingForm from "./AddListingForm";
import MyButton from "@/MY_COMPONENTS/MyButton";
import MyIconButton from "@/components/MyIconButton";
import MapView, { MapItem } from "@/MY_COMPONENTS/MapView";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import MyHeaderLabel from "@/components/MyHeaderLabel";

// Types - Matches your exact database schema
interface ApiListing {
  product_id: string;
  tenant_id: string;
  sku: string;
  title: string;
  slug: string;
  description: string | null;
  brand: string | null;
  price: number;
  price_unit: "month" | "night" | "hour" | "day" | "total";
  sale_price: number | null;
  cost_price: number | null;
  stock: number;
  reserved: number;
  low_stock_threshold: number;
  reorder_point: number;
  type: "physical" | "digital" | "service" | "rental";
  attributes: string; // JSON string of amenities
  image_url: string | null;
  gallery: string | null;
  booking_rules: string | null;
  weight: number | null;
  dimensions: string | null;
  property_category: "residential" | "commercial";
  property_type: "apartment" | "room" | "office" | "shop" | "villa" | "commercial" | "house" | "land";
  property_status: "available" | "rented" | "maintenance" | "coming_soon" | "sold";
  bedrooms: number | null;
  bathrooms: number | null;
  area_sqft: number | null;
  area_sqm: number | null;
  floor_number: number | null;
  total_floors: number | null;
  year_built: number | null;
  furnished: "unfurnished" | "semi-furnished" | "fully-furnished" | null;
  parking_spots: number;
  pet_friendly: number; // 0 or 1
  immediate_available: number; // 0 or 1
  minimum_lease_months: number | null;
  maximum_lease_months: number | null;
  deposit_amount: number | null;
  maintenance_fee: number | null;
  utilities_included: number; // 0 or 1
  viewing_count: number;
  favorite_count: number;
  latitude: number;
  longitude: number;
  address_line1: string | null;
  address_line2: string | null;
  city: string;
  island: string;
  postal_code: string | null;
  virtual_tour_url: string | null;
  status: string;
  is_new: number; // 0 or 1
  is_featured: number; // 0 or 1
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
}

// Extended type with id alias for CustomListingsPage
export interface ExtendedApiListing extends ApiListing {
  id: string; // Alias for product_id
}

// Transformed listing for UI components
interface TransformedListing {
  id: string;
  product_id: string;
  image_url: string;
  title: string;
  price: number;
  category: string;
  priceUnit: "month" | "night" | "hour" | "day" | "total";
  location: string;
  address_line1: string;
  timeAgo: string;
  type: "residential" | "commercial" | "short-stay";
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  amenities: string[];
  status: "available" | "rented" | "maintenance" | "coming_soon" | "sold";
  latitude: number;
  longitude: number;
  images?: string[];
  rating?: number;
  reviews?: number;
  isNew: boolean;
  isFeatured: boolean;
  viewingCount: number;
  favoriteCount: number;
  furnished?: string;
  parkingSpots: number;
  petFriendly: boolean;
  immediateAvailable: boolean;
  utilitiesIncluded: boolean;
  minimumLeaseMonths?: number;
  maximumLeaseMonths?: number;
  depositAmount?: number;
  maintenanceFee?: number;
}

export default function ListingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { open, closeAll } = useMyDialog();

  // Move all hooks BEFORE any conditional returns
  const transformListing = useCallback((apiListing: ApiListing): TransformedListing => {
    // Parse amenities from JSON string
    let amenities: string[] = [];
    try {
      amenities = JSON.parse(apiListing.attributes || "[]");
    } catch (e) {
      console.error("Failed to parse amenities:", e);
      amenities = [];
    }

    // Determine listing type for UI
    let type: "residential" | "commercial" | "short-stay" = "residential";
    if (apiListing.property_category === "commercial") {
      type = "commercial";
    } else if (apiListing.price_unit === "night" || apiListing.price_unit === "hour" || apiListing.price_unit === "day") {
      type = "short-stay";
    }

    // Generate time ago from created_at timestamp
    const getTimeAgo = (timestamp: string): string => {
      const now = new Date();
      const created = new Date(timestamp);
      const diffMs = now.getTime() - created.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    };

    // Build location string
    const locationParts = [
      apiListing.address_line1,
      apiListing.island,
      apiListing.city
    ].filter(Boolean);
    
    const location = locationParts.join(", ");

    return {
      id: apiListing.product_id,
      product_id: apiListing.product_id,
      image_url: apiListing.image_url || apiListing.title,
      title: apiListing.title,
      price: apiListing.price,
      priceUnit: apiListing.price_unit,
      category: apiListing.property_category,
      location: location || "Location not specified",
      address_line1: apiListing.address_line1 || `${apiListing.island || apiListing.city || "Unknown"}`,
      timeAgo: getTimeAgo(apiListing.created_at),
      type: type,
      bedrooms: apiListing.bedrooms || undefined,
      bathrooms: apiListing.bathrooms || undefined,
      area: apiListing.area_sqft || apiListing.area_sqm || undefined,
      amenities: amenities,
      status: apiListing.property_status,
      latitude: apiListing.latitude,
      longitude: apiListing.longitude,
      isNew: apiListing.is_new === 1,
      isFeatured: apiListing.is_featured === 1,
      viewingCount: apiListing.viewing_count,
      favoriteCount: apiListing.favorite_count,
      furnished: apiListing.furnished || undefined,
      parkingSpots: apiListing.parking_spots,
      petFriendly: apiListing.pet_friendly === 1,
      immediateAvailable: apiListing.immediate_available === 1,
      utilitiesIncluded: apiListing.utilities_included === 1,
      minimumLeaseMonths: apiListing.minimum_lease_months || undefined,
      maximumLeaseMonths: apiListing.maximum_lease_months || undefined,
      depositAmount: apiListing.deposit_amount || undefined,
      maintenanceFee: apiListing.maintenance_fee || undefined,
    };
  }, []);

  const handleViewDetails = useCallback((data: ApiListing) => {
    console.log("View details for:", data);
    open({
      content: <ListingDetails listing={transformListing(data)} onClose={closeAll} />
    });
  }, [open, closeAll, transformListing]);

  const handleLike = useCallback((listing: ExtendedApiListing | TransformedListing) => {
    console.log("Liked listing:", listing);
    // Call API to toggle favorite
    // fetch(`/api/products/${listing.product_id}/favorite`, { method: 'POST' })
  }, []);

  // Create a proper renderMapView function that receives the listings array
  const renderMapView = useCallback((listings: ExtendedApiListing[]) => {
    return (
      <div className="h-[600px]">
        <MapView
          items={listings.map(listing => ({
            id: listing.product_id,
            title: listing.title,
            lat: listing.latitude ?? 4.175666,
            lng: listing.longitude ?? 73.508889,
            image: listing.image_url,
            extra: listing,
            label: listing.title ? `🏠 ${listing.title}` : "📍"
          }))}
          height="100%"
          zoom={13}
          center={[4.175278, 73.508889]}
          renderPopup={(mapItem: MapItem) => {
            const listing = mapItem.extra as ExtendedApiListing;
            return (
              <div className="space-y-3 text-center min-w-[220px] p-2">
                {mapItem.image && (
                  <img
                    src={mapItem.image}
                    alt={mapItem.title}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                )}
                <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                  {mapItem.title}
                </h3>
                {listing?.price && (
                  <p className="text-purple-600 font-bold text-lg">
                    MVR {listing.price}/{listing.price_unit || 'month'}
                  </p>
                )}
                {listing?.bedrooms && (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {listing.bedrooms} bed{listing.bedrooms !== 1 ? 's' : ''} • {listing.bathrooms} bath{listing.bathrooms !== 1 ? 's' : ''}
                  </p>
                )}
                <button
                  onClick={() => handleViewDetails(listing)}
                  className="mt-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm transition-colors w-full font-medium"
                >
                  View Details
                </button>
              </div>
            );
          }}
        />
      </div>
    );
  }, [handleViewDetails]);

  // 👈 Now conditionally return AFTER all hooks have been called
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading authentication...</p>
        </div>
      </div>
    );
  }

  // Debug log to check user state
  console.log("🔍 ListingsPage - User object:", user);
  console.log("🔍 ListingsPage - Auth loading:", authLoading);

  return (
    <CustomListingsPage<ExtendedApiListing>
      // Core configuration
      endpoint={`${MY_ENDPOINTS.PRODUCT_ENDPOINTS.GET_PRODUCTS.path}`} // Fetch only rental properties
      method="GET"
      queryKey="listings"
      
      // Header configuration
      headerText="Rent smarter in the Maldives"
      headerSubtext="Apartments, rooms, and commercial spaces across Malé and Hulhumalé."
      headerIcon={Home}
      headerBadge="Find your perfect space"
      showHeader={true}
      headerButtons={
        <>
          <MyIconButton
            link="/listings/new"
            label="Browse"
            icon={<SearchIcon className="w-5 h-5" />}
            iconPosition="left"
            gap="gap-3"
            buttonStyle="px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:shadow-lg transition-all"
          />
          {user?.user_id ? (
            <MyDialogButton 
              button={
                <button className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all">
                  List Property
                </button>
              }
              dialogContent={<AddListingForm />} 
            />
          ) : (
            <MyButton label="List Property" onClick={() => navigate("/login")} styles="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all" />
          )}
        </>
      }
      
      // Hero stats
      heroStats={[]}
      
      // Search configuration
      searchPlaceholder="Search listings by title, location, or amenities..."
      enableSearch={true}
      searchDebounceMs={500}
      
      // View configuration
      defaultView="grid"
      gridCols={{ mobile: 1, sm: 2, lg: 3, xl: 4 }}
      
      // Filter configuration - Using exact schema field names
      enableFilters={true}
      filterConfig={{
        property_category: [
          { value: "all", label: "Any Type" },
          { value: "residential", label: "Residential" },
          { value: "commercial", label: "Commercial" },
        ],
        price_unit: [
          { value: "all", label: "Any Duration" },
          { value: "month", label: "Monthly" },
          { value: "night", label: "Nightly" },
          { value: "day", label: "Daily" },
          { value: "hour", label: "Hourly" },
          { value: "total", label: "One-time" },
        ],
        property_type: [
          { value: "all", label: "Any Property Type" },
          { value: "apartment", label: "Apartment" },
          { value: "room", label: "Room" },
          { value: "office", label: "Office" },
          { value: "shop", label: "Shop" },
          { value: "villa", label: "Villa" },
          { value: "house", label: "House" },
          { value: "land", label: "Land" },
        ],
        bedrooms: [
          { value: "any", label: "Any Bedrooms" },
          { value: "0", label: "Studio" },
          { value: "1", label: "1 Bedroom" },
          { value: "2", label: "2 Bedrooms" },
          { value: "3", label: "3+ Bedrooms" },
        ],
        furnished: [
          { value: "any", label: "Any Furnishing" },
          { value: "fully-furnished", label: "Fully Furnished" },
          { value: "semi-furnished", label: "Semi-Furnished" },
          { value: "unfurnished", label: "Unfurnished" },
        ],
      }}
      
      // Categories configuration
      enableCategories={true}
      categories={[
        { id: "residential", label: "Residential", icon: Home },
        { id: "commercial", label: "Commercial", icon: Building2 },
        { id: "short-stay", label: "Short Stay", icon: Clock },
      ]}
      categoriesLabel="Property Types"
      
      // Map configuration - Fixed
      enableMapView={true}
      renderMapView={renderMapView}
      
      // Sort options
      sortOptions={[
        { value: "newest", label: "Newest first" },
        { value: "price_low", label: "Price: Low to High" },
        { value: "price_high", label: "Price: High to Low" },
        { value: "popular", label: "Most Popular" },
        { value: "viewing_count", label: "Most Viewed" },
      ]}
      defaultSort="newest"
      
      // Sticky configuration
      navbarHeight={64}
      
      // Render function for all view types
      renderItem={(apiListing: ExtendedApiListing, view: "grid" | "list" | "map") => {
        const listing = transformListing(apiListing);

        switch (view) {
          case "grid":
            return (
              <ListingCardGrid
                listing={listing}
                onViewDetails={() => handleViewDetails(apiListing)}
                onLike={() => handleLike(apiListing)}
                onClick={() => handleViewDetails(apiListing)}
              />
            );
          case "list":
            return (
              <ListingCardList
                listing={listing}
                onViewDetails={() => handleViewDetails(apiListing)}
                onLike={() => handleLike(apiListing)}
                onClick={() => handleViewDetails(apiListing)}
              />
            );
          default:
            return null;
        }
      }}
      
      // Empty state
      renderEmptyState={() => (
        <div className="text-center py-16">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Home className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No listings found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            We couldn't find any properties matching your criteria. Try adjusting your filters or search terms.
          </p>
        </div>
      )}
      
      // Loading state
      renderLoadingState={() => (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 dark:bg-gray-700 rounded-xl h-48 mb-3"></div>
              <div className="bg-gray-200 dark:bg-gray-700 rounded h-4 w-3/4 mb-2"></div>
              <div className="bg-gray-200 dark:bg-gray-700 rounded h-4 w-1/2"></div>
            </div>
          ))}
        </div>
      )}
      
      // Event handlers
      onSearch={(searchTerm) => {
        console.log("Searching for:", searchTerm);
      }}
      
      onFilterChange={(filters) => {
        console.log("Filters changed:", filters);
      }}
      
      onCategoryChange={(categoryId) => {
        console.log("Category changed:", categoryId);
      }}
      
      onItemClick={(apiListing: ExtendedApiListing) => {
        handleViewDetails(apiListing);
      }}
      
      onLike={(apiListing: ExtendedApiListing) => {
        handleLike(apiListing);
      }}
      
      // Additional query parameters - Using exact schema field names
      additionalParams={{ 
        limit: 20,
        type: "rental,service", // Match your schema: 'rental' or 'service'
        status: "available" // Only show available listings
      }}
      
      // Styling
      containerClassName="min-h-screen bg-gray-50 dark:bg-gray-900"
      contentClassName="py-8"
      headerClassName=""
      filterBarClassName=""
    />
  );
}